import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldAlert, CheckCircle2, Settings2, X } from 'lucide-react';

/**
 * AI health + low-balance banner (admin only).
 *
 * Two jobs: warn BEFORE the Universal LLM key budget runs out (month-to-date
 * spend vs the budget you set here), and shout immediately if AI calls are
 * already failing — so we hear it from the dashboard, not from a parent.
 */
const STYLES = {
  degraded: { wrap: 'border-rose-200 bg-rose-50', text: 'text-rose-800', Icon: ShieldAlert },
  critical: { wrap: 'border-rose-200 bg-rose-50', text: 'text-rose-800', Icon: ShieldAlert },
  warning: { wrap: 'border-amber-200 bg-amber-50', text: 'text-amber-800', Icon: AlertTriangle },
  ok: { wrap: 'border-emerald-100 bg-emerald-50/60', text: 'text-emerald-800', Icon: CheckCircle2 },
};

export function AiHealthBanner() {
  const [health, setHealth] = useState(null);
  const [editing, setEditing] = useState(false);
  const [budget, setBudget] = useState('');
  const [warnAt, setWarnAt] = useState('');

  const load = () => apiGet('/admin/ai-health').then(h => {
    setHealth(h);
    setBudget(String(h.monthly_budget_inr || ''));
    setWarnAt(String(h.warn_at_pct || 80));
  }).catch(() => setHealth(null));

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // keep it live while the tab is open
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    try {
      await apiPut('/admin/ai-budget', {
        monthly_budget_inr: parseFloat(budget) || 0,
        warn_at_pct: parseInt(warnAt, 10) || 80,
      });
      toast.success('AI budget alert updated');
      setEditing(false);
      load();
    } catch { toast.error('Could not save the AI budget'); }
  };

  if (!health) return null;
  const s = STYLES[health.level] || STYLES.ok;
  const { Icon } = s;

  const headline = health.degraded
    ? 'AI features are failing right now'
    : health.level === 'critical'
      ? `AI spend has passed your monthly budget (₹${health.spend_inr} of ₹${health.monthly_budget_inr})`
      : health.level === 'warning'
        ? `AI spend is at ${health.pct_used}% of this month's budget (₹${health.spend_inr} of ₹${health.monthly_budget_inr})`
        : `AI healthy · ₹${health.spend_inr} spent this month${health.monthly_budget_inr ? ` of ₹${health.monthly_budget_inr}` : ' (no budget set)'}`;

  return (
    <div className={`rounded-2xl border p-3.5 mb-4 ${s.wrap}`} data-testid="ai-health-banner" data-level={health.level}>
      <div className="flex items-start gap-2.5">
        <Icon className={`w-4.5 h-4.5 mt-0.5 flex-shrink-0 ${s.text}`} />
        <div className="min-w-0 flex-1">
          <p className={`font-heading text-sm font-semibold ${s.text}`} data-testid="ai-health-headline">{headline}</p>
          {health.degraded ? (
            <p className="text-[11px] text-rose-700 font-body mt-0.5">
              Students are seeing "AI temporarily unavailable" — the rest of the app is unaffected. {health.hint}
            </p>
          ) : (
            <p className="text-[11px] text-slate-500 font-body mt-0.5">
              {health.top_call_types?.length
                ? `Biggest AI costs this month: ${health.top_call_types.slice(0, 3).map(c => `${c.call_type} ₹${c.inr}`).join(' · ')}`
                : 'No AI usage recorded this month yet.'}
            </p>
          )}
          {health.monthly_budget_inr > 0 && (
            <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden max-w-sm">
              <div className={`h-full transition-all duration-500 ${health.pct_used >= 100 ? 'bg-rose-500' : health.pct_used >= health.warn_at_pct ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, health.pct_used)}%` }} />
            </div>
          )}

          {editing && (
            <div className="flex items-end gap-2 mt-2.5 flex-wrap">
              <label className="text-[10px] font-body text-slate-500">
                Monthly AI budget (₹)
                <Input data-testid="ai-budget-input" value={budget} onChange={e => setBudget(e.target.value)}
                  className="h-8 w-28 mt-0.5 rounded-lg text-xs bg-white" />
              </label>
              <label className="text-[10px] font-body text-slate-500">
                Warn at (%)
                <Input data-testid="ai-warn-input" value={warnAt} onChange={e => setWarnAt(e.target.value)}
                  className="h-8 w-20 mt-0.5 rounded-lg text-xs bg-white" />
              </label>
              <Button size="sm" className="h-8 text-[11px]" data-testid="ai-budget-save" onClick={save}>Save</Button>
            </div>
          )}
        </div>
        <button data-testid="ai-health-configure" onClick={() => setEditing(v => !v)} title="Set the alert threshold"
          className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white/60 flex items-center justify-center flex-shrink-0 transition-colors">
          {editing ? <X className="w-3.5 h-3.5" /> : <Settings2 className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default AiHealthBanner;
